import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Plus,
  Search,
  Users,
  ChevronRight,
  Loader2,
} from "lucide-react";
import apiClient from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import toast from "react-hot-toast";

const inputClass =
  "w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none";

export default function RecruitmentClientsPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // "all" | "client" | "prospect"

  // Create Prospect Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [newProspect, setNewProspect] = useState({
    name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
  });

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/recruitment/companies");
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load companies:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchCompanies();
  }, []);

  const handleCreateProspect = async (e) => {
    e.preventDefault();
    setCreateError("");
    if (!newProspect.name.trim()) {
      setCreateError("Le nom de l'entreprise est obligatoire.");
      return;
    }
    setCreating(true);
    try {
      const contacts = [];
      if (newProspect.contact_name.trim()) {
        contacts.push({
          id: `contact_${Date.now()}`,
          name: newProspect.contact_name.trim(),
          email: newProspect.contact_email.trim() || undefined,
          phone: newProspect.contact_phone.trim() || undefined,
          role: "Contact principal",
        });
      }
      await apiClient.post("/recruitment/companies/prospects", {
        name: newProspect.name.trim(),
        contacts,
        notes: newProspect.notes.trim() || undefined,
      });
      toast.success("Prospect créé avec succès !");
      setShowCreateModal(false);
      setNewProspect({
        name: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        notes: "",
      });
      await fetchCompanies();
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "Échec de la création du prospect.");
    } finally {
      setCreating(false);
    }
  };

  const filteredCompanies = useMemo(() => {
    return companies.filter((c) => {
      if (typeFilter !== "all" && c.company_type !== typeFilter) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const nameMatch = (c.name || "").toLowerCase().includes(q);
        const contactMatch = (c.contacts || []).some((cnt) =>
          (cnt.name || "").toLowerCase().includes(q)
        );
        return nameMatch || contactMatch;
      }
      return true;
    });
  }, [companies, typeFilter, searchQuery]);

  return (
    <div className="space-y-6 pb-12" data-testid="recruitment-clients-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6 text-blue-400" />
            Clients & Prospects
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Gérez le portefeuille des clients actifs et prospects recrutement avec l&apos;historique complet des besoins et présentations.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-md hover:bg-blue-500 transition-all self-start sm:self-auto"
          data-testid="create-prospect-button"
        >
          <Plus className="h-4 w-4" />
          + Nouveau prospect
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par nom d'entreprise ou contact..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/80 border border-white/10 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            data-testid="clients-search-input"
          />
        </div>

        <div className="flex items-center gap-1.5 p-1 bg-white/[0.03] border border-white/10 rounded-xl self-start">
          {[
            { id: "all", label: "Tous" },
            { id: "client", label: "Clients Actifs (Pilotage)" },
            { id: "prospect", label: "Prospects" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTypeFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                typeFilter === tab.id
                  ? "bg-blue-600 text-white shadow"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Companies List */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="glass rounded-2xl p-12 border border-white/10 text-center space-y-2">
          <Building2 className="h-8 w-8 text-slate-500 mx-auto" />
          <p className="text-sm font-semibold text-white">Aucune entreprise trouvée</p>
          <p className="text-xs text-slate-400">
            {searchQuery
              ? `Aucun résultat pour « ${searchQuery} »`
              : "Créez votre premier prospect ou importez des besoins."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCompanies.map((comp) => {
            const isClient = comp.company_type === "client";
            return (
              <Link
                key={comp.id}
                to={`/admin/recrutement/clients/${comp.id}`}
                className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all flex flex-col justify-between group"
                data-testid={`company-card-${comp.id}`}
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="text-base font-bold text-white group-hover:text-blue-300 transition-colors">
                      {comp.name}
                    </h2>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border shrink-0 ${
                        isClient
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
                          : "bg-amber-500/10 text-amber-300 border-amber-500/20"
                      }`}
                    >
                      {isClient ? "Client Actif" : "Prospect"}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-xs text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-slate-500" />
                      <span>{comp.contacts?.length || 0} interlocuteur(s)</span>
                    </div>
                    {comp.contacts?.[0] && (
                      <p className="text-[11px] text-slate-300 pl-5">
                        Principal : <strong>{comp.contacts[0].name}</strong>
                        {comp.contacts[0].role && ` (${comp.contacts[0].role})`}
                      </p>
                    )}
                  </div>
                </div>

                <div className="pt-4 mt-4 border-t border-white/5 flex items-center justify-between text-xs text-blue-400 font-semibold group-hover:text-blue-300">
                  <span>Consulter la fiche & l&apos;historique</span>
                  <ChevronRight className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Modal Nouveau Prospect */}
      <Dialog open={showCreateModal} onOpenChange={(open) => !open && !creating && setShowCreateModal(false)}>
        <DialogContent className="max-w-lg bg-[#101426] text-white border-white/10 rounded-2xl w-[calc(100%_-_2rem)]">
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-400" />
            Nouveau prospect
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Enregistré dans le vivier Recrutement sans polluer le référentiel Pilotage.
          </DialogDescription>

          <form onSubmit={handleCreateProspect} className="space-y-4 pt-2">
            {createError && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300">
                {createError}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">
                Nom de l&apos;entreprise *
              </label>
              <input
                type="text"
                required
                placeholder="Ex: Groupe Thales, Startup AI..."
                value={newProspect.name}
                onChange={(e) => setNewProspect({ ...newProspect, name: e.target.value })}
                className={inputClass}
                data-testid="prospect-name-input"
              />
            </div>

            <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 space-y-3">
              <span className="text-xs font-semibold text-slate-300 block">
                Interlocuteur initial (facultatif)
              </span>
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  type="text"
                  placeholder="Nom & prénom"
                  value={newProspect.contact_name}
                  onChange={(e) =>
                    setNewProspect({ ...newProspect, contact_name: e.target.value })
                  }
                  className={inputClass}
                />
                <input
                  type="email"
                  placeholder="Email professionnel"
                  value={newProspect.contact_email}
                  onChange={(e) =>
                    setNewProspect({ ...newProspect, contact_email: e.target.value })
                  }
                  className={inputClass}
                />
                <input
                  type="tel"
                  placeholder="Téléphone"
                  value={newProspect.contact_phone}
                  onChange={(e) =>
                    setNewProspect({ ...newProspect, contact_phone: e.target.value })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">
                Notes commerciales
              </label>
              <textarea
                rows={2}
                placeholder="Contexte, canal de prospection..."
                value={newProspect.notes}
                onChange={(e) => setNewProspect({ ...newProspect, notes: e.target.value })}
                className={inputClass}
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={creating || !newProspect.name.trim()}
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white shadow-md flex items-center gap-2 disabled:opacity-50"
              >
                {creating ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Plus className="h-3.5 w-3.5" />
                    Créer le prospect
                  </>
                )}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
